import { useMemo } from "react";
import type { SetURLSearchParams } from "react-router";
import { type DocumentVersionSummary } from "~/api/types";
import { useTags } from "~/api/swr/useTags";
import {
  buildFilterParamKeysForField,
  buildSearchParamValuesFromFilters,
  dateFilterKeys,
  multiSelectFilterKeys,
  nullableDateFilterKeys,
  FilterOperatorEnum,
  type FilterCondition,
  type DateFilterKey,
  type FilterField,
  type FilterOperator,
  type NullableDateFilterKey,
  isFilterFieldValueValid,
  isNullableDateFilterKey,
  type MultiSelectFilterKey,
  parseFilters,
} from "../helpers/documentFilters";
import { printFullName } from "../helpers/user";

interface UseDocumentVersionFilterParams {
  documentVersions: DocumentVersionSummary[];
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

export interface SelectFieldData {
  values: string[];
  labelsByValue: Record<string, string>;
}

export interface DocumentVersionFiltersModel {
  dateFilterKeys: typeof dateFilterKeys;
  multiSelectFilterKeys: typeof multiSelectFilterKeys;
  nullableDateFilterKeys: typeof nullableDateFilterKeys;
  multiSelectFieldData: Record<MultiSelectFilterKey, SelectFieldData>;
  activeFilters: FilterCondition[];
  getFilterValue: (
    field: FilterField,
    operator: FilterOperator,
  ) => string | undefined;
  setFilterValue: (
    field: FilterField,
    operator: FilterOperator,
    value?: string,
  ) => void;
  getInValues: (field: MultiSelectFilterKey) => string[];
  setInValues: (field: MultiSelectFilterKey, values: string[]) => void;
  isNullFilterActive: (field: NullableDateFilterKey) => boolean;
  setNullFilter: (field: NullableDateFilterKey, active: boolean) => void;
  isDateRangeInvalid: (filterKey: DateFilterKey) => boolean;
  removeFilter: (field: FilterField) => void;
}

const pageParam = "page";
type ActiveFilterValuesByField = Partial<
  Record<FilterField, Partial<Record<FilterOperator, string>>>
>;

const buildSelectFieldData = (
  entries: Array<[string, string]>,
): SelectFieldData => {
  const labelsByValue = entries.reduce<Record<string, string>>(
    (acc, [value, label]) => {
      acc[value] = label;
      return acc;
    },
    {},
  );

  return {
    labelsByValue,
    values: Object.keys(labelsByValue).sort((left, right) =>
      labelsByValue[left]!.localeCompare(labelsByValue[right]!),
    ),
  };
};

export const useDocumentVersionFilters = ({
  documentVersions,
  searchParams,
  setSearchParams,
}: UseDocumentVersionFilterParams): DocumentVersionFiltersModel => {
  const { data: tags } = useTags();

  const activeFilters = useMemo(
    () => parseFilters(searchParams),
    [searchParams],
  );
  const activeFilterValuesByField = useMemo(
    () =>
      activeFilters.reduce<ActiveFilterValuesByField>((acc, filter) => {
        if (typeof filter.value !== "string") {
          return acc;
        }

        acc[filter.field] = {
          ...(acc[filter.field] ?? {}),
          [filter.operator]: filter.value,
        };
        return acc;
      }, {}),
    [activeFilters],
  );

  const multiSelectFieldData = useMemo<
    Record<MultiSelectFilterKey, SelectFieldData>
  >(
    () => ({
      status: {
        values: Array.from(
          new Set(documentVersions.map((dv) => dv.status)),
        ).sort(),
        labelsByValue: {},
      },
      tier: buildSelectFieldData(
        documentVersions.map((dv) => [
          dv.document.tier.id.toString(),
          dv.document.tier.title,
        ]),
      ),
      category: buildSelectFieldData(
        documentVersions.map((dv) => [
          dv.document.category.id.toString(),
          dv.document.category.title,
        ]),
      ),
      tag: buildSelectFieldData(
        (tags ?? []).map((tag) => [tag.id.toString(), tag.title]),
      ),
      owner: buildSelectFieldData(
        documentVersions.map((dv) => [
          dv.document.owner.userId.toString(),
          printFullName(dv.document.owner),
        ]),
      ),
      publisher: buildSelectFieldData(
        documentVersions.flatMap((dv) => {
          const publisher = dv.document.publisher;

          if (!publisher) {
            return [];
          }

          return [[publisher.userId.toString(), printFullName(publisher)]];
        }),
      ),
    }),
    [documentVersions, tags],
  );

  const setSearchParamsValues = (
    values: Record<string, string | string[] | undefined>,
    { resetPage = false }: { resetPage?: boolean } = {},
  ) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(values).forEach(([key, value]) => {
      next.delete(key);
      if (value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v.length > 0) next.append(key, v);
        });
      } else if (value.length > 0) {
        next.set(key, value);
      }
    });
    if (resetPage) {
      next.set(pageParam, "1");
    }
    setSearchParams(next);
  };

  const getFilterValue = (field: FilterField, operator: FilterOperator) =>
    activeFilterValuesByField[field]?.[operator];

  const setFilterValue = (
    field: FilterField,
    operator: FilterOperator,
    value?: string,
  ) => {
    let nextFilters = activeFilters.filter(
      (filter) => !(filter.field === field && filter.operator === operator),
    );

    if (
      isNullableDateFilterKey(field) &&
      operator !== FilterOperatorEnum.isNull
    ) {
      nextFilters = nextFilters.filter(
        (filter) =>
          !(
            filter.field === field &&
            filter.operator === FilterOperatorEnum.isNull
          ),
      );
    }

    if (value && value.length > 0) {
      if (!isFilterFieldValueValid(field, value)) {
        return;
      }

      nextFilters.push({
        field,
        operator,
        value,
      });
    }

    setSearchParamsValues(buildSearchParamValuesFromFilters(nextFilters), {
      resetPage: true,
    });
  };

  const getInValues = (field: MultiSelectFilterKey) => {
    const eqFilter = activeFilters.find(
      (filter) =>
        filter.field === field && filter.operator === FilterOperatorEnum.eq,
    );
    if (eqFilter && typeof eqFilter.value === "string") {
      return [eqFilter.value];
    }

    const inFilter = activeFilters.find(
      (filter) =>
        filter.field === field && filter.operator === FilterOperatorEnum.in,
    );
    if (inFilter && Array.isArray(inFilter.value)) {
      return inFilter.value;
    }

    return [];
  };

  const setInValues = (field: MultiSelectFilterKey, values: string[]) => {
    const nextFilters = activeFilters.filter(
      (filter) =>
        !(
          filter.field === field &&
          (filter.operator === FilterOperatorEnum.eq ||
            filter.operator === FilterOperatorEnum.in)
        ),
    );

    if (values.length === 1) {
      const [singleValue] = values;
      if (!singleValue) {
        throw new Error("Missing value for eq operator");
      }

      nextFilters.push({
        field,
        operator: FilterOperatorEnum.eq,
        value: singleValue,
      });
    } else if (values.length > 1) {
      nextFilters.push({
        field,
        operator: FilterOperatorEnum.in,
        value: values,
      });
    }

    setSearchParamsValues(buildSearchParamValuesFromFilters(nextFilters), {
      resetPage: true,
    });
  };

  const isNullFilterActive = (field: NullableDateFilterKey) =>
    activeFilters.some(
      (filter) =>
        filter.field === field && filter.operator === FilterOperatorEnum.isNull,
    );

  const setNullFilter = (field: NullableDateFilterKey, active: boolean) => {
    if (active) {
      const nextFilters = activeFilters.filter(
        (filter) => filter.field !== field,
      );
      nextFilters.push({
        field,
        operator: FilterOperatorEnum.isNull,
        value: "null",
      });
      setSearchParamsValues(buildSearchParamValuesFromFilters(nextFilters), {
        resetPage: true,
      });
    } else {
      const nextFilters = activeFilters.filter(
        (filter) =>
          !(
            filter.field === field &&
            filter.operator === FilterOperatorEnum.isNull
          ),
      );
      setSearchParamsValues(buildSearchParamValuesFromFilters(nextFilters), {
        resetPage: true,
      });
    }
  };

  const isDateRangeInvalid = (filterKey: DateFilterKey) => {
    const gte = getFilterValue(filterKey, FilterOperatorEnum.gte);
    const lte = getFilterValue(filterKey, FilterOperatorEnum.lte);
    if (!gte || !lte) {
      return false;
    }

    if (
      !isFilterFieldValueValid(filterKey, gte) ||
      !isFilterFieldValueValid(filterKey, lte)
    ) {
      return false;
    }

    return new Date(gte) > new Date(lte);
  };

  const removeFilter = (field: FilterField) => {
    setSearchParamsValues(
      buildFilterParamKeysForField(field).reduce<
        Record<string, string | undefined>
      >((acc, paramKey) => {
        acc[paramKey] = undefined;
        return acc;
      }, {}),
      { resetPage: true },
    );
  };

  return {
    dateFilterKeys,
    multiSelectFilterKeys,
    nullableDateFilterKeys,
    multiSelectFieldData,
    activeFilters,
    getFilterValue,
    setFilterValue,
    getInValues,
    setInValues,
    isNullFilterActive,
    setNullFilter,
    isDateRangeInvalid,
    removeFilter,
  };
};
